import { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { AirportAutocomplete, AirportAutocompleteProps } from "@airhelp/airport-autocomplete";
import { AirportOption } from "@airhelp/react";
import { FieldValues, useController, UseControllerProps } from "react-hook-form";
import { handleFieldChange } from "./handleFieldChange";
import { useAirportSearch } from "../hooks/useAirportSearch";
import { trackSearchAirport } from "../analytics/track";
import { useTranslation } from "react-i18next";

type ControlledAirportAutocompleteProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> &
    Partial<AirportAutocompleteProps<AirportOption>> & { isRtl: boolean };

const DEBOUNCE_TIMEOUT = 300;

export function ControlledAirportAutocomplete<T extends FieldValues>({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  formControlStyles,
  isRtl,
  ...props
}: ControlledAirportAutocompleteProps<T>) {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, DEBOUNCE_TIMEOUT);
  const { airports = [], isError, isFetching } = useAirportSearch(debouncedSearchTerm);
  const { t } = useTranslation();

  const airportOptions = useMemo(
    () => airports?.map((airport) => ({ value: airport })),
    [airports],
  );

  const { field } = useController({
    name,
    rules,
    shouldUnregister,
    defaultValue,
    control,
  });
  const showMenu = !!searchTerm && airportOptions;

  return (
    <AirportAutocomplete
      {...props}
      name={field.name}
      menuIsOpen={!!showMenu}
      isLoading={isFetching}
      options={airportOptions as AirportOption[]}
      defaultValue={field.value ? { value: field.value } : null}
      handleChange={(option) => {
        handleFieldChange(field, option?.value);
        if (option?.value) {
          trackSearchAirport(field.name, option, searchTerm);
        }
      }}
      onInputChange={(searchTerm) => {
        setSearchTerm(searchTerm);
      }}
      isInvalid={isError}
      colorScheme="warning"
      errorMessage={t("components.airline_autocomplete.search_error")}
      noOptionText={t("steps.itinerary_details.no_results_found")}
      formControlStyles={{
        ...formControlStyles,
        css: isRtl
          ? {
              ".chakra-text": {
                direction: "rtl",
              },
              "[class*=indicatorContainer] svg": {
                marginLeft: 8,
              },
              "div[role=listbox]": {
                "p.chakra-text": {
                  direction: "ltr",
                },
              },
            }
          : {},
      }}
    />
  );
}
